// Crew Settings User Tab - user management table (indigo theme, Figma design)
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { MoreHorizontal, UserPlus } from 'lucide-react';

import { ErrorToast, SuccessToast } from 'components';
import { DeleteModal } from 'features';
import DepartmentService from 'api/department';
import UserService from 'api/identity/user';
import UserManagement_Modal from './crew-invite-user-modal';

// ---- helpers ----

const getStatusLabel = (code) => {
  switch (code) {
    case 1: return 'INVITED';
    case 2: return 'ACTIVE';
    case 3: return 'LOCKED';
    case 4: return 'BANNED';
    case 5: return 'DELETED';
    default: return 'UNSPECIFIED';
  }
};

const STATUS_STYLES = {
  ACTIVE:      'tw-bg-green-100 tw-text-green-700',
  INVITED:     'tw-bg-blue-100 tw-text-blue-700',
  LOCKED:      'tw-bg-gray-100 tw-text-gray-600',
  BANNED:      'tw-bg-red-100 tw-text-red-700',
  DELETED:     'tw-bg-red-100 tw-text-red-700',
  UNSPECIFIED: 'tw-bg-gray-100 tw-text-gray-500',
};

const DOT_STYLES = {
  ACTIVE:      'tw-bg-green-500',
  INVITED:     'tw-bg-blue-500',
  LOCKED:      'tw-bg-gray-400',
  BANNED:      'tw-bg-red-500',
  DELETED:     'tw-bg-red-500',
  UNSPECIFIED: 'tw-bg-gray-400',
};

const StatusBadge = ({ statusCode }) => {
  const label = getStatusLabel(statusCode);
  return (
    <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium ${STATUS_STYLES[label] || STATUS_STYLES.UNSPECIFIED}`}>
      <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-full ${DOT_STYLES[label] || DOT_STYLES.UNSPECIFIED}`} />
      {label}
    </span>
  );
};

// ---- action menu ----

const ActionMenu = ({ user, currentUserInfo, onEdit, onDelete, onReInvite, onActivate }) => {
  const [open, setOpen] = useState(false);
  const isAdmin = currentUserInfo?.metadata?.role === 'admin';
  const canManage = isAdmin || (currentUserInfo?.metadata?.role !== 'admin' && user?.metadata?.role !== 'admin');

  if (!canManage) return null;

  return (
    <div className="tw-relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="tw-p-1 tw-rounded tw-border-0 tw-bg-transparent tw-cursor-pointer hover:tw-bg-gray-100 tw-transition-colors"
      >
        <MoreHorizontal className="tw-w-4 tw-h-4 tw-text-gray-500" />
      </button>
      {open && (
        <>
          <div className="tw-fixed tw-inset-0 tw-z-10" onClick={() => setOpen(false)} />
          <div className="tw-absolute tw-right-0 tw-top-7 tw-z-20 tw-bg-white tw-rounded-lg tw-shadow-lg tw-border tw-border-gray-200 tw-py-1 tw-min-w-36">
            {(user.status === 3 || user.status === 4 || user.status === 5) && (
              <button onClick={() => { onActivate(user.id); setOpen(false); }}
                className="tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50 tw-border-0 tw-bg-transparent tw-cursor-pointer">
                Activate user
              </button>
            )}
            {user.status === 1 && (
              <button onClick={() => { onReInvite(user.id); setOpen(false); }}
                className="tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50 tw-border-0 tw-bg-transparent tw-cursor-pointer">
                Re-invite user
              </button>
            )}
            {user.status !== 5 && (
              <button onClick={() => { onEdit(user); setOpen(false); }}
                className="tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50 tw-border-0 tw-bg-transparent tw-cursor-pointer">
                Edit user
              </button>
            )}
            {user.status !== 5 && (
              <button onClick={() => { onDelete(user); setOpen(false); }}
                className="tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-red-600 hover:tw-bg-red-50 tw-border-0 tw-bg-transparent tw-cursor-pointer">
                Delete user
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ---- main component ----

const CrewSettingsUserTab = ({ users, fetchUsers }) => {
  const userInfo = useSelector((state) => state.general.user);
  const { writeDepts } = useSelector((state) => state.general?.userRightsObj?.users || {});
  const { userDepartments } = useSelector((state) => state.general);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [objDepts, setObjDepts] = useState({});
  const [deptDropOptions, setDeptDropOptions] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });

  useEffect(() => { fetchAllDepartments(); }, []);
  useEffect(() => { setWriteDepts(); }, [userDepartments]);
  useEffect(() => { if (!isModalOpen) setSelectedUser(null); }, [isModalOpen]);

  const fetchAllDepartments = async () => {
    try {
      const { data: { data } } = await DepartmentService.getAll({ filters: { isDeleted: false } });
      const mapped = {};
      data.forEach((d) => { mapped[d._id] = d.name; });
      setObjDepts(mapped);
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
    }
  };

  const setWriteDepts = () => {
    if (!userDepartments) return;
    const depts = userDepartments.filter((d) => writeDepts?.includes(d._id));
    setDeptDropOptions(depts.map((d) => ({ value: d._id, text: d.name })));
  };

  const renderDepts = (deptIds = []) =>
    deptIds.map((id) => objDepts[id]).filter(Boolean).join(', ') || 'All';

  const onEditClick = (user) => { setSelectedUser(user); setIsModalOpen(true); };

  const onDeleteConfirm = async () => {
    try {
      await UserService.deleteUser(deleteModal.user.id);
      await fetchUsers();
      setDeleteModal({ open: false, user: null });
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
    }
  };

  const onActivate = async (id) => {
    try {
      await UserService.updateStatus({ id, status: 2 });
      await fetchUsers();
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
    }
  };

  const onReInvite = async (id) => {
    try {
      await UserService.resendActivationEmail(id);
      toast.success(<SuccessToast message="User invite sent" />);
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
    }
  };

  return (
    <div className="tw-p-6">
      {isModalOpen && (
        <UserManagement_Modal
          objDepts={objDepts}
          fetchUsers={fetchUsers}
          toggleModal={setIsModalOpen}
          isModalToggled={isModalOpen}
          deptDropOptions={deptDropOptions}
          user={selectedUser}
        />
      )}

      <DeleteModal
        toggleModal={() => setDeleteModal({ open: false, user: null })}
        isModalToggled={deleteModal.open}
        header="Are you sure you want to delete this user?"
        content={<span>Deleted users won't be able to login anymore and can be recovered.</span>}
        onDelete={onDeleteConfirm}
      />

      {/* Toolbar */}
      <div className="tw-flex tw-justify-end tw-mb-5">
        <button
          onClick={() => setIsModalOpen(true)}
          className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-indigo-700 tw-text-white tw-text-sm tw-font-medium tw-rounded-lg tw-border-0 tw-cursor-pointer hover:tw-bg-indigo-800 tw-transition-colors"
        >
          <UserPlus className="tw-w-4 tw-h-4" />
          Invite new user
        </button>
      </div>

      {/* Table */}
      <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-overflow-hidden">
        <div className="tw-overflow-x-auto">
          <table className="tw-w-full tw-text-left tw-border-collapse">
            <thead>
              <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-text-xs tw-uppercase tw-tracking-wider tw-text-gray-500 tw-font-semibold">
                <th className="tw-p-4">Name</th>
                <th className="tw-p-4">Email</th>
                <th className="tw-p-4">Departments</th>
                <th className="tw-p-4">Role</th>
                <th className="tw-p-4">Status</th>
                <th className="tw-p-4">Created on</th>
                <th className="tw-p-4 tw-text-center">Action</th>
              </tr>
            </thead>
            <tbody className="tw-divide-y tw-divide-gray-100">
              {!users?.length ? (
                <tr>
                  <td colSpan={7} className="tw-p-8 tw-text-center tw-text-sm tw-text-gray-400">
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:tw-bg-gray-50 tw-transition-colors">
                    <td className="tw-p-4">
                      <span className="tw-text-sm tw-font-semibold tw-text-gray-900">
                        {user?.metadata?.name}
                      </span>
                    </td>
                    <td className="tw-p-4">
                      <span className="tw-text-sm tw-text-gray-600">{user?.email}</span>
                    </td>
                    <td className="tw-p-4">
                      <span className="tw-text-sm tw-text-gray-600">
                        {renderDepts(user?.departments?.departments)}
                      </span>
                    </td>
                    <td className="tw-p-4">
                      <span className="tw-text-sm tw-text-gray-600 tw-uppercase">
                        {user?.metadata?.role}
                      </span>
                    </td>
                    <td className="tw-p-4">
                      <StatusBadge statusCode={user?.status} />
                    </td>
                    <td className="tw-p-4">
                      <span className="tw-text-sm tw-text-gray-600">
                        {moment(user?.createdAt).format('DD MMM YYYY')}
                      </span>
                    </td>
                    <td className="tw-p-4 tw-text-center">
                      <ActionMenu
                        user={user}
                        currentUserInfo={userInfo}
                        onEdit={onEditClick}
                        onDelete={(u) => setDeleteModal({ open: true, user: u })}
                        onReInvite={onReInvite}
                        onActivate={onActivate}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CrewSettingsUserTab;
